import { useState } from 'react'

import { useUnit } from 'effector-react'
import { useMutation, useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { audioService } from '@/modules/chatting/service/audio'
import { chatsService } from '@/modules/chatting/service/chats'
import type { Audio, MediaAttachment } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

import { DIALOG_TYPE } from '../constants'
import { getContentBytes, normalizeContentType } from './utils'

export interface UseAddAudioParams {
  onClose: () => void
  onAddAudio: (attachment: MediaAttachment) => void
}

export const useAddAudio = ({ onClose, onAddAudio }: UseAddAudioParams) => {
  const [selectedAudio, setSelectedAudio] = useState<Audio | null>(null)
  const creatorId = useUnit($creatorId)

  const {
    data: audios = [],
    isLoading,
    refetch,
    isError,
  } = useQuery(
    ['audios', creatorId],
    () => audioService.getAudios(creatorId!),
    {
      enabled: !!creatorId,
      onError: (error) => {
        handleError(error, 'Something went wrong while getting audios')
      },
    },
  )

  const getAudioFileMutation = useMutation(audioService.getAudioFile, {
    onError: (error) => {
      handleError(error, "Couldn't download audio file")
    },
  })
  const uploadMediaMutation = useMutation(
    (audio: Audio) => {
      return getAudioFileMutation
        .mutateAsync({ id: audio.id, creatorId: creatorId! })
        .then((base64) => {
          const contentType = normalizeContentType(audio.contentType)
          const extension = contentType.split('/')[1]
          const fileName = `${audio.name}.${extension}`

          const blob = new Blob(getContentBytes(base64), {
            type: audio.contentType,
          })
          const file = new File([blob], fileName, {
            type: contentType,
          })

          return chatsService
            .uploadMediaInChunks({
              creatorId: creatorId!,
              fileName,
              file,
            })
            .then(async (media) => {
              const url = URL.createObjectURL(blob)

              // @ts-ignore
              onAddAudio({ blob, media, duration: audio.duration, url })
            })
        })
    },
    {
      onError: (error) => {
        handleError(error, "Couldn't upload audio file")
      },
    },
  )

  const handleSelect = (audio: Audio) => {
    if (selectedAudio?.id === audio.id) {
      setSelectedAudio(null)
    } else {
      setSelectedAudio(audio)
    }
  }

  const handleAdd = async () => {
    if (!selectedAudio) return

    uploadMediaMutation.mutateAsync(selectedAudio).then(onClose)
  }

  const getAudioFile = (audio: Audio) => {
    return getAudioFileMutation
      .mutateAsync({ id: audio.id, creatorId: creatorId! })
      .then(
        (base64) =>
          new Blob(getContentBytes(base64), { type: audio.contentType }),
      )
  }

  const handleCloseModal = () => {
    setSelectedAudio(null)
  }

  const handleSetFile = (file: File) => {
    openDialog({
      type: DIALOG_TYPE.UPLOAD_AUDIO,
      audio: file,
    })
  }

  return {
    handleSelect,
    selectedAudio,
    handleAdd,
    isAdding: uploadMediaMutation.isLoading,
    handleCloseModal,
    getAudioFile,
    audios,
    isLoading,
    handleSetFile,
    isError,
    refetch,
  }
}
